import React, { useState, useRef } from 'react';
import useData from '../../hooks/useData';
import useToast from '../../hooks/useToast';
import { getStatusPillClass, getPriorityPillClass, getDeadlineClass, getPriorityColor, getAlterationPillClass } from '../../utils/helpers';
import CustomDatePicker from '../ui/CustomDatePicker';
import { EditIcon, CalendarIcon, CheckCircleIcon, ArrowUpIcon, MinusIcon, ArrowDownIcon, TagIcon, RefreshIcon } from '../ui/Icons';
import useClickOutside from '../../hooks/useClickOutside';
// --- INÍCIO DA ALTERAÇÃO 1: Importando o Portal e o Backdrop ---
import Portal from '../ui/Portal';
import Backdrop from '../ui/Backdrop';
// --- FIM DA ALTERAÇÃO 1 ---

const PillSelectorDropdown = ({ options, currentValue, onSelect, getPillClass, placeholder = "Nenhuma" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    useClickOutside(dropdownRef, () => setIsOpen(false));
    const currentLabel = options.find(o => o.value === currentValue)?.label || placeholder;

    return (
        <div className="relative" ref={dropdownRef}>
            <button onClick={() => setIsOpen(!isOpen)} className="w-full text-center cursor-pointer flex items-center justify-center gap-2 p-1 rounded-md border-2 border-transparent hover:border-white/20 hover:bg-white/10 transition-all duration-200">
                <span className={`px-2 py-1 rounded-full text-xs ${getPillClass(currentValue)}`}>{currentLabel}</span>
                <EditIcon size={14} className="text-gray-400" />
            </button>
            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-max bg-gray-800/90 backdrop-blur-lg border border-white/10 rounded-lg shadow-xl p-2 z-20 animate-fade-in">
                    <div className="space-y-1">
                        {options.map(option => (
                            <button
                                key={option.value}
                                onClick={() => { onSelect(option.value); setIsOpen(false); }}
                                className={`w-full text-left px-2 py-1 rounded-md text-xs transition-colors hover:bg-white/20 ${getPillClass(option.value)}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- INÍCIO DA ALTERAÇÃO 2: Refatorando o DatePickerDropdown para usar Portal ---
const DatePickerDropdown = ({ value, onSave, icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const openCalendar = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const calendarWidth = 288; // w-72
            
            // Posiciona o calendário abaixo do botão, alinhado à direita
            setPosition({
                top: rect.bottom + 8, // 8px de margem
                left: rect.right - calendarWidth
            });
        }
        setIsOpen(true);
    };

    const closeCalendar = () => setIsOpen(false);

    const handleDateSelect = (date) => {
        onSave(date);
        closeCalendar();
    };

    return (
        <>
            <div className="flex items-center group" ref={buttonRef}>
                {icon && <div className="mr-3 flex-shrink-0">{icon}</div>}
                <span className="text-gray-400 font-medium">Prazo:</span>
                <div className="flex-1 min-w-0 ml-2 relative">
                    <button onClick={openCalendar} className="w-full flex items-center justify-end gap-2 p-1 rounded-md text-sm border-2 border-transparent group-hover:border-white/20 group-hover:bg-white/10 transition-all duration-200">
                        <span className={getDeadlineClass(value)}>{value ? new Date(value).toLocaleDateString('pt-BR', {timeZone: 'UTC'}) : "Nenhum"}</span>
                        <EditIcon size={14} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </button>
                </div>
            </div>

            {isOpen && (
                <Portal>
                    <Backdrop onClick={closeCalendar} />
                    <div
                        className="fixed z-[60] animate-fade-in"
                        style={{ top: `${position.top}px`, left: `${position.left}px` }}
                    >
                        <CustomDatePicker value={value} onChange={handleDateSelect} />
                    </div>
                </Portal>
            )}
        </>
    );
};
// --- FIM DA ALTERAÇÃO 2 ---

const StatusWidget = ({ task }) => {
    const { handleUpdateTask } = useData();
    const { showToast } = useToast();

    const handleSave = (field, value) => {
        if (task[field] !== value) {
            if (field === 'status' && value === 'Concluído') {
                const actionText = 'marcou a tarefa como Concluída e a arquivou.';
                handleUpdateTask(task.id, { status: 'Concluído', isArchived: true }, actionText);
                showToast("Tarefa Concluída e Arquivada!");
            } else {
                handleUpdateTask(task.id, { [field]: value }, `alterou ${field} para ${value}`);
                showToast(`${field.charAt(0).toUpperCase() + field.slice(1)} atualizado!`);
            }
        }
    };

    const handleToggleAlteration = () => {
        const newValue = !task.isAlteration;
        const historyAction = newValue ? 'marcou a tarefa como uma alteração.' : 'desmarcou a tarefa como uma alteração.';
        handleUpdateTask(task.id, { isAlteration: newValue }, historyAction);
        showToast(newValue ? 'Tarefa marcada como alteração.' : 'Marcação de alteração removida.');
    };

    const statusOptions = [ { value: 'A Fazer', label: 'A Fazer' }, { value: 'Em Progresso', label: 'Em Progresso' }, { value: 'Revisão', label: 'Revisão' }, { value: 'Concluído', label: 'Concluído' } ];
    const priorityOptions = [ { value: 'Baixa', label: 'Baixa' }, { value: 'Média', label: 'Média' }, { value: 'Alta', label: 'Alta' } ];

    const getPriorityIcon = (priority) => {
        const color = getPriorityColor(priority);
        switch (priority) {
            case 'Alta': return <ArrowUpIcon size={16} style={{ color }} />;
            case 'Média': return <MinusIcon size={16} style={{ color }} />;
            case 'Baixa': return <ArrowDownIcon size={16} style={{ color }} />;
            default: return <MinusIcon size={16} className="text-gray-400" />;
        }
    };

    return (
        <div className="space-y-3 text-sm">
            <h3 className="text-sm font-semibold text-gray-400 mb-2">Status & Detalhes</h3>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <CheckCircleIcon size={16} className="text-gray-400" />
                    <span className="text-gray-400 font-medium">Status:</span>
                </div>
                <div className="w-36">
                    <PillSelectorDropdown 
                        options={statusOptions}
                        currentValue={task.status}
                        onSelect={(value) => handleSave('status', value)}
                        getPillClass={getStatusPillClass}
                    />
                </div>
            </div>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {getPriorityIcon(task.priority)}
                    <span className="text-gray-400 font-medium">Prioridade:</span>
                </div>
                <div className="w-36">
                     <PillSelectorDropdown 
                        options={priorityOptions}
                        currentValue={task.priority}
                        onSelect={(value) => handleSave('priority', value)}
                        getPillClass={getPriorityPillClass}
                    />
                </div>
            </div>
            
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <TagIcon size={16} className="text-gray-400" />
                    <span className="text-gray-400 font-medium">Etiqueta:</span>
                </div>
                <div className="w-auto">
                    <button 
                        onClick={handleToggleAlteration}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs transition-colors ${getAlterationPillClass(task.isAlteration)}`}
                    >
                        <RefreshIcon size={14} />
                        <span>{task.isAlteration ? 'Alteração' : 'Marcar Alteração'}</span>
                    </button>
                </div>
            </div>

            <DatePickerDropdown
                value={task.deadline || ''}
                onSave={(v) => handleSave('deadline', v)}
                icon={<CalendarIcon size={16} className="text-gray-400" />}
            />
        </div>
    );
};

export default StatusWidget;